import {detectScrollIntoViewContainerSupport} from './detect-scroll-into-view-container-support';
import {scrollIntoViewNearest} from './scrollIntoViewNearest';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createLazyValue<F extends () => any>(fn: F): F {
    const gen = (function* () {
        const val = fn();
        while (true) {
            yield val;
        }
    })();

    return (() => gen.next().value) as F;
}

const getSupport = createLazyValue(detectScrollIntoViewContainerSupport);

/**
 * Scroll an element into view while optionally requesting the experimental `{container: 'nearest'}` behavior.
 *
 * Behavior:
 * - If `options?.container !== 'nearest'`, we directly delegate to the native `element.scrollIntoView` API (sync path).
 * - If `container: 'nearest'` is requested we lazily detect native support once:
 *   - On supported browsers we call the native API (sync) and still return a resolved promise.
 *   - On unsupported browsers we compute the minimal scroll for ONLY the nearest scrollable ancestor (ponyfill) and
 *     perform that scroll respecting logical / physical scroll margins.
 *
 * Notes:
 * - Function is `async` because detection requires a rAF tick; you may choose to not await if ordering is unimportant.
 * - The TypeScript DOM lib may not yet include the experimental `container` property (hence `@ts-expect-error` in callers).
 * - SSR: When `document` is absent detection short‑circuits to `true` and we simply delegate to native (no-op server side).
 *
 * @param element Target DOM element to reveal.
 * @param options Standard `ScrollIntoViewOptions` plus experimental `{container: 'nearest'}`.
 * @returns Promise resolving after the action is taken (or native call dispatched).
 */
export async function scrollIntoView(
    element: HTMLElement,
    options?: ScrollIntoViewOptions & {container?: 'nearest'},
): Promise<void> { // explicit for clarity
    if (options?.container !== 'nearest') {
        element.scrollIntoView(options);
        return;
    }
    const isSupported = await getSupport();
    if (isSupported) {
        element.scrollIntoView(options);
    } else {
        scrollIntoViewNearest(element, options);
    }
}
